/**
 * The "?" button — doctrine 8's per-screen half.
 *
 * Tips are migrated data (src/api/manual.ts serves help_tips), fetched once per session
 * and resolved locally, so a "?" opens instantly and cannot fail while the screen behind
 * it is already on fire. Every tip that names a manual chapter carries a link to it, so
 * help is always one hop from the full explanation.
 *
 * If a key has no tip — a screen shipped ahead of its documentation — the button is not
 * rendered at all. A "?" that opens an empty box is worse than no "?".
 */
import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useHelpTip } from '../lib/useApi.ts';
import { Icon } from './Icon.tsx';
import { useDismissOnOutside, useEscapeKey } from './useOverlay.tsx';

type Position = { top: number; left: number };

export type HelpTipProps = {
  /** `<pageKey>.<anchorKey>`, matching help_tips (page_key, anchor_key). */
  helpKey: string;
  /** Overrides the accessible label; defaults to the tip's own title. */
  label?: string;
};

export function HelpTip({ helpKey, label }: HelpTipProps): ReactElement | null {
  const tip = useHelpTip(helpKey);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<Position | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEscapeKey(open, () => setOpen(false));
  useDismissOnOutside(open, popRef, () => setOpen(false));

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const width = Math.min(340, window.innerWidth - 24);
    // Keep it on screen: flip left when it would run off the right edge.
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
    setPos({ top: rect.bottom + 6, left });
  }, [open]);

  if (!tip) return null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="nd-helptip-btn"
        aria-label={label ?? `What is ${tip.title}?`}
        aria-expanded={open}
        title={tip.title}
        onClick={(event) => {
          // A "?" inside a card header or a table cell must not also trigger whatever the
          // header or the row does.
          event.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <Icon name="help" size={14} />
      </button>

      {open && pos
        ? createPortal(
            <div
              ref={popRef}
              className="nd-helptip"
              role="dialog"
              aria-label={tip.title}
              style={{ top: pos.top, left: pos.left }}
            >
              <div className="nd-helptip-title">{tip.title}</div>
              <TipBody body={tip.body} onNavigate={() => setOpen(false)} />
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/**
 * A deliberately tiny markdown renderer: paragraphs, `code`, **bold**, and links. The tip
 * bodies are ours and they are short; pulling in a markdown parser for four constructs
 * would be a dependency to keep patched forever for no reader benefit.
 */
export function TipBody({ body, onNavigate }: { body: string; onNavigate?: () => void }): ReactElement {
  const paragraphs = body.split(/\n{2,}/).filter((p) => p.trim().length > 0);
  return (
    <>
      {paragraphs.map((paragraph, index) => (
        <p key={index}>{renderInline(paragraph.trim(), onNavigate)}</p>
      ))}
    </>
  );
}

const INLINE = /(\[[^\]]+\]\([^)]+\))|(`[^`]+`)|(\*\*[^*]+\*\*)/g;

function renderInline(text: string, onNavigate?: () => void): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  INLINE.lastIndex = 0;

  while ((match = INLINE.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const token = match[0];

    if (token.startsWith('[')) {
      const close = token.indexOf('](');
      const label = token.slice(1, close);
      const href = token.slice(close + 2, -1);
      out.push(
        href.startsWith('/') ? (
          <Link key={`${match.index}-l`} to={href} onClick={onNavigate}>
            {label}
          </Link>
        ) : (
          <a key={`${match.index}-l`} href={href} target="_blank" rel="noreferrer noopener">
            {label}
          </a>
        ),
      );
    } else if (token.startsWith('`')) {
      out.push(<code key={`${match.index}-c`}>{token.slice(1, -1)}</code>);
    } else {
      out.push(<strong key={`${match.index}-b`}>{token.slice(2, -2)}</strong>);
    }
    last = match.index + token.length;
  }

  if (last < text.length) out.push(text.slice(last));
  return out;
}

export default HelpTip;
